import React, { Fragment, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import CircularProgress from "@mui/material/CircularProgress";
import { fetchPolicyImportList, importPolicy } from "../helper";
import { Stack } from "@mui/material";
import { editPolicy } from "../helper";
import { useUserContext } from "../../../context/auth/UserContext";

interface PolicyImportInterface {
  policyID: number;
  policyName: string;
  policyContent: string;
  version: string;
  policyStatus: string;
  approvalDate: Date;
}

export const PolicyImport = (props: {
  openDialog: boolean;
  handleCancel: () => void;
  refreshList: () => void;
  policyList: string[];
}) => {
  const [open, setOpen] = useState(false);
  const { userId, companyProfileID } = useUserContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checked, setChecked] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [importPolicyList, setImportPolicyList] = useState<any[]>([]);
  const [existingPolicies, setExsistingPolicies] = useState<
    PolicyImportInterface[]
  >([]);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openImportConfirmation: false,
    openSuccessDialogBox: false,
  });

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setChecked([]);
    } else {
      setSelectAll(true);
      const newChecked = importPolicyList.map((ele) => ele.templateId);
      setChecked(newChecked);
    }
  };

  const handleClose = () => {
    if (props.handleCancel) {
      props.handleCancel();
    }
  };

  const getSelectedPolicies = () => {
    return importPolicyList.filter((ele) => checked.includes(ele.templateId));
  };

  const getExistingPolicy = (selectedPolicy: any[]) => {
    // console.log("props.policy", props.policyList);

    return selectedPolicy
      .filter((policy: any) => {
        return props.policyList.some(
          (ele: any) => ele.policyName === policy.policyName
        );
      })
      .map((policy: any) => {
        const matchedPolicy: any = props.policyList.find(
          (ele: any) => ele.policyName === policy.policyName
        );
        return {
          ...policy,
          policyID: matchedPolicy.policyID, // Add policyId from the matched policy
        };
      });
  };

  const handleImport = async () => {
    const selectedPolicy = getSelectedPolicies();
    const isExsistingPolicy = await getExistingPolicy(selectedPolicy);
    setExsistingPolicies(isExsistingPolicy);
    // console.log("filteredExsistingPolicy", filteredExsistingPolicy);
    if (isExsistingPolicy.length > 0) {
      const existingPolicyNames = isExsistingPolicy
        .map((policy) => policy.policyName)
        .join(", ");
      setDialogState({
        title: "Confirm",
        body: `The following policies are already existing in the list: <strong>${existingPolicyNames}</strong>. If you import, they will be overwritten. Are you sure?`,
        action: "Import",
        actionCancel: "Cancel",
        openImportConfirmation: true,
        openSuccessDialogBox: false,
      });
    } else {
      importPolicies();
    }
  };

  const importPolicies = async () => {
    setIsLoading(true);
    const selectedPolicy = getSelectedPolicies();
    const newlyImportPolicy = selectedPolicy.filter(
      (policy) =>
        !existingPolicies.some(
          (existingPolicy) => existingPolicy.policyName === policy.policyName
        )
    );

    let hasError = false;
    const date = new Date();

    //Import new policy
    if (newlyImportPolicy.length > 0) {
      const payload = newlyImportPolicy.map((ele) => {
        return {
          policyName: ele.policyName,
          policyContent: ele.policyContent,
          policyVersion: ele.version + "",
          policyStatus: "Not Approved",
          policyApprovedBy: null,
          createDate: date,
          createdBy: userId,
          isEditedByUserId: "0",
          isTemplateImport: true,
          companyProfileID: companyProfileID,
          isInclude: true,
        };
      });
      // console.log("payload selectedImportPolicyFiltered", payload);
      const resImport = await importPolicy(payload);
      if (!resImport.succeeded) {
        hasError = true;
      }
    }

    //Update existing policy
    if (existingPolicies.length > 0) {
      for (const ele of existingPolicies) {
        const payload = {
          policyID: ele.policyID,
          policyName: ele.policyName,
          policyContent: ele.policyContent,
          policyVersion: ele?.version + "",
          policyStatus: "Not Approved",
          policyApprovedBy: null,
          policyApprovalDate: ele.approvalDate,
          updateDate: date,
          updateBy: userId,
          isEditedByUserId: "0",
          isTemplateImport: true,
          isInclude: true,
        };

        try {
          const resUpdate = await editPolicy(payload);
          if (!resUpdate.succeeded) {
            hasError = true;
            break;
          }
        } catch (error) {
          console.error("@Error", error);
          hasError = true;
          break;
        }
      }
    }

    if (hasError) {
      setIsLoading(false);
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openImportConfirmation: false,
        openSuccessDialogBox: true,
      });
    } else {
      setIsLoading(false);
      setDialogState({
        title: "Success",
        body: "Policy successfully imported",
        action: "OK",
        actionCancel: "",
        openImportConfirmation: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const handleCloseDialog = () => {
    if (dialogState.title !== "Error") {
      setDialogState({
        ...dialogState,
        openImportConfirmation: false,
        openSuccessDialogBox: false,
      });
      props.refreshList();
      handleClose();
    } else {
      setDialogState({
        ...dialogState,
        openImportConfirmation: false,
        openSuccessDialogBox: false,
      });
    }
  };

  const fetchPolicyImport = async () => {
    try {
      const res = await fetchPolicyImportList();
      setImportPolicyList(res);
    } catch (error) {
      console.log("@@Error:", error);
    }
  };

  useEffect(() => {
    setOpen(props.openDialog);
    if (props.openDialog) {
      fetchPolicyImport();
    }
  }, [props.openDialog]);

  return (
    <Fragment>
      <Dialog open={open} onClose={handleClose} keepMounted>
        <DialogTitle sx={{ fontSize: "1.1rem" }}>Import Policy</DialogTitle>
        <DialogContent sx={{ height: "350px", overflowY: "auto" }}>
          <Stack direction="row" sx={{ mx: 2 }}>
            <Checkbox
              size="small"
              edge="start"
              checked={selectAll}
              onChange={handleSelectAll}
              tabIndex={-1}
              disableRipple
              sx={{ p: 0, mr: 3 }}
            />
            <span>Select All</span>
          </Stack>
          <List sx={{ width: "300px", bgcolor: "background.paper" }}>
            {!isLoading || importPolicyList.length > 0 ? (
              importPolicyList.map((ele) => {
                const labelId = `checkbox-list-label-${ele}`;

                return (
                  <ListItem key={ele.templateId} disablePadding disableGutters>
                    <ListItemButton
                      role={undefined}
                      onClick={handleToggle(ele.templateId)}
                      dense
                    >
                      <ListItemIcon sx={{ minWidth: "2rem" }}>
                        <Checkbox
                          size="small"
                          edge="start"
                          checked={checked.indexOf(ele.templateId) !== -1}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ "aria-labelledby": labelId }}
                          sx={{ p: 0 }}
                        />
                      </ListItemIcon>
                      <ListItemText id={labelId} primary={ele.policyName} />
                    </ListItemButton>
                  </ListItem>
                );
              })
            ) : (
              <div>
                <CircularProgress />
              </div>
            )}
          </List>
        </DialogContent>

        <DialogActions sx={{ padding: "0.5rem 1.5rem" }}>
          <Button
            size="small"
            variant="contained"
            className="table-btn"
            disabled={isLoading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            className="table-btn"
            disabled={checked.length === 0 || isLoading}
            onClick={handleImport}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
      {
        //Confirmation
        <DialogBoxComponent
          openDialogBox={dialogState.openImportConfirmation}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={importPolicies}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //success
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
